import i18nFactory from '@yandex-int/i18n';
import { get } from 'lodash';
import { Epic, ofType } from 'redux-observable';
import { concat, EMPTY, from, Observable, of } from 'rxjs';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/concat';
import 'rxjs/add/operator/concatMap';
import 'rxjs/add/operator/merge';
import {
    catchError,
    filter,
    map,
    mergeMap,
    switchMap,
    takeUntil
} from 'rxjs/operators';

import { Action } from '../../actions';
import { showCommonDataFetchError } from '../../actions/common';
import {
    ACCEPT_REJECT_DEALS,
    AcceptRejectDeals,
    clearCampaignsDeals,
    ClearCampaignsDealsAction,
    clearClientsDeals,
    ClearClientsDealsAction,
    LOAD_CAMPAIGNS_DEALS,
    LOAD_CLIENTS_DEALS,
    LoadCampaignsDealsAction,
    LoadClientsDealsAction,
    OPEN_DEAL_INSPECTOR,
    OpenDealInspector,
    REQUSET_DEALS_ROW,
    RESET_DEALS_DATA,
    resetDealsData,
    showInspectorDataApiError,
    showInspectorErrors,
    UPDATE_DEALS,
    UPDATE_DEALS_SOURCE_PARAMS,
    updateCampaignsDeals,
    UpdateCampaignsDealsAction,
    updateClientsDeals,
    UpdateClientsDealsAction,
    updateDealDataInspector,
    UpdateDeals,
    updateDealsData,
    updateDealsDataItem,
    UpdateDealsSourceParams
} from '../../actions/deals';
import { RequestGridRow } from '../../actions/grid-screen';
import { getValueFromApiFormat } from '../../mappers/common/common';
import { selectDealsCacheKey } from '../../selectors/deals';
import { ApiMethodExecutingError } from '../../service/Errors';
import { RequestIndexResult } from '../../service/SegmentManager';
import { EpicDeps } from '../../service/StoreFactory';
import { DropdownChooserItem } from '../../typings/common';
import { DnaEpic } from '../../typings/epic';
import { State } from '../../typings/state';

import * as keyset from './deals.i18n';

const i18n = i18nFactory(keyset);

const requestDealsEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(REQUSET_DEALS_ROW),
        map(({ index }: RequestGridRow) => {
            return deps.segmentManager.requestIndex(index);
        }),
        filter(res => res !== null),
        filter(({ isNeedFetch }: RequestIndexResult) => isNeedFetch),
        mergeMap(
            ({ offset, count }: RequestIndexResult): Observable<Action> => {
                const state = store$.value;
                const { sourceParams } = state.deals;
                const cacheKey = selectDealsCacheKey(state);

                return from(
                    deps.dealsDataProvider.fetchDealsDataSegment(
                        sourceParams,
                        offset,
                        count,
                        cacheKey
                    )
                ).pipe(
                    takeUntil(action$.ofType(RESET_DEALS_DATA)),
                    mergeMap(
                        (response): Observable<Action> => {
                            const storeData = store$.value;
                            const { data, total, allItemsIds } = response;

                            const deals = data.reduce((res, item, pos) => {
                                res[offset + pos] = item;

                                return res;
                            }, storeData.deals.gridData.data.slice());

                            deps.segmentManager.setTotal(total);

                            return of(
                                updateDealsData(
                                    {
                                        data: deals,
                                        total,
                                        allItemsIds
                                    },
                                    response.cacheKey
                                )
                            );
                        }
                    )
                );
            }
        ),
        catchError(
            (err: ApiMethodExecutingError): Observable<Action> => {
                return of(showCommonDataFetchError(err));
            }
        )
    );
};

const updateDealsSourceParamsEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = action$ => {
    return action$.pipe(
        ofType(UPDATE_DEALS_SOURCE_PARAMS),
        map((action: UpdateDealsSourceParams): Action => resetDealsData())
    );
};

// todo DIRECT-78850
const resetDealsDataEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    action$.ofType(RESET_DEALS_DATA).subscribe(() => {
        deps.segmentManager.reset();
    });

    return EMPTY;
};

const dealDetailEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(OPEN_DEAL_INSPECTOR),
        mergeMap(
            (action: OpenDealInspector): Observable<Action> => {
                const storeData = store$.value;
                const login = get(storeData, 'deals.sourceParams.login');
                const dealId = get(
                    storeData,
                    `deals.gridData.allItemsIds[${action.index}]`
                );

                return from(
                    deps.dealsDataProvider.getDealsDetail(login, [dealId])
                ).pipe(
                    mergeMap(
                        (detailDeals): Observable<Action> =>
                            of(updateDealDataInspector(detailDeals[0]))
                    ),
                    catchError(
                        (): Observable<Action> => {
                            return of(
                                showInspectorDataApiError(i18n('request-error'))
                            );
                        }
                    )
                );
            }
        )
    );
};

const loadDealsCampaignsEpic: DnaEpic = (
    action$,
    store$,
    deps
): Observable<UpdateCampaignsDealsAction | ClearCampaignsDealsAction> => {
    return action$.pipe(
        ofType(LOAD_CAMPAIGNS_DEALS),
        switchMap(({ client }: LoadCampaignsDealsAction) => {
            return from(
                deps.dealsDataProvider.loadDealsCampaignsByClient(client)
            ).pipe(
                map((campaigns: DropdownChooserItem[]) => {
                    const items: DropdownChooserItem[] = campaigns.map(
                        ({ id, name }) => ({
                            id: String(id),
                            name,
                            addition: String(id)
                        })
                    );

                    return updateCampaignsDeals(items, client);
                }),
                catchError(() => of(clearCampaignsDeals(client)))
            );
        })
    );
};

const clientsDealsLoadEpic: DnaEpic = (
    action$,
    store$,
    deps
): Observable<UpdateClientsDealsAction | ClearClientsDealsAction> => {
    return action$.pipe(
        ofType(LOAD_CLIENTS_DEALS),
        switchMap(({ login }: LoadClientsDealsAction) => {
            return from(
                deps.dealsDataProvider.clientsDealsLoadByLogin(login)
            ).pipe(
                map((logins: string[]) => {
                    const items: DropdownChooserItem[] = logins.map(name => ({
                        id: name,
                        name
                    }));

                    return updateClientsDeals(items);
                }),
                catchError(err => of(clearClientsDeals()))
            );
        })
    );
};

const updateDealsEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(UPDATE_DEALS),
        mergeMap(
            ({
                updateObject,
                dealIds,
                login
            }: UpdateDeals): Observable<Action> => {
                return from(
                    deps.dealsDataProvider.updateDeals(login, updateObject)
                ).pipe(
                    filter(({ success }) => {
                        if (!success) {
                            throw new Error(i18n('request-error'));
                        }
                        return success;
                    }),
                    mergeMap(
                        ({ errors = [] }): Observable<Action> => {
                            return from(
                                deps.dealsDataProvider.getDealsDetail(
                                    login,
                                    dealIds
                                )
                            ).pipe(
                                mergeMap(
                                    (detailDeals): Observable<Action> => {
                                        return concat(
                                            of(
                                                updateDealDataInspector(
                                                    detailDeals[0],
                                                    errors,
                                                    updateObject
                                                )
                                            ),
                                            of(
                                                updateDealsDataItem(
                                                    detailDeals[0].deal
                                                )
                                                // cyn@todo: reset cacheKey
                                            )
                                        );
                                    }
                                ),
                                catchError(
                                    (): Observable<Action> => {
                                        return of(
                                            showInspectorErrors(
                                                errors,
                                                updateObject
                                            )
                                        );
                                    }
                                )
                            );
                        }
                    ),
                    catchError(
                        (): Observable<Action> => {
                            return of(
                                showInspectorDataApiError(i18n('request-error'))
                            );
                        }
                    )
                );
            }
        )
    );
};

const acceptRejectDealsEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(ACCEPT_REJECT_DEALS),
        mergeMap(
            (action: AcceptRejectDeals): Observable<Action> => {
                const { updateObject, isAccept, login, dealIds } = action;

                return from(
                    (updateObject
                        ? deps.dealsDataProvider
                              .updateDeals(login, updateObject)
                              .then(({ errors, success }) => {
                                  if (!success) {
                                      throw new Error(i18n('request-error'));
                                  }
                                  return errors;
                              })
                        : Promise.resolve([])
                    ).then(savedErrors => {
                        return savedErrors.length
                            ? savedErrors
                            : deps.dealsDataProvider
                                  .acceptRejectDeals(login, dealIds, isAccept)
                                  .then(({ errors }) => errors);
                    })
                ).pipe(
                    mergeMap(
                        (errors): Observable<Action> => {
                            return from(
                                deps.dealsDataProvider.getDealsDetail(
                                    login,
                                    dealIds
                                )
                            ).pipe(
                                mergeMap(
                                    (detailDeals): Observable<Action> => {
                                        return concat(
                                            of(
                                                updateDealDataInspector(
                                                    detailDeals[0],
                                                    errors
                                                )
                                            ),
                                            of(
                                                updateDealsDataItem(
                                                    detailDeals[0].deal
                                                )
                                            ),
                                            of(
                                                showInspectorErrors(
                                                    errors,
                                                    getValueFromApiFormat(
                                                        updateObject
                                                    )
                                                )
                                            )
                                        );
                                    }
                                )
                            );
                        }
                    ),
                    catchError(
                        (): Observable<Action> =>
                            of(showInspectorDataApiError(i18n('request-error')))
                    )
                );
            }
        )
    );
};

export {
    updateDealsSourceParamsEpic,
    dealDetailEpic,
    clientsDealsLoadEpic,
    updateDealsEpic,
    loadDealsCampaignsEpic,
    acceptRejectDealsEpic,
    requestDealsEpic,
    resetDealsDataEpic
};
